import type { EmployeePick } from './types'

/**
 * Katılımcı **görüntüleme sırası** (kullanıcı isteği 30.09.2026): önce **unvan grubu koduna göre
 * artan**, aynı grupta **ada göre Türkçe alfabetik**.
 *
 * <h2>Neden istemcide de var</h2>
 *
 * Asıl kural sunucudadır (`meeting/ParticipantDisplayOrder`) ve kaydedilmiş toplantıya uygulanır.
 * Ama tutanak **oluşturma/düzenleme** ekranındaki katılımcı listesi henüz kaydedilmemiştir: orada
 * sunucu devrede değildir. Kopya olmasaydı — ve 30.09.2026'ya kadar yoktu — kullanıcı seçtiği
 * kişileri **ekleme sırasında** görür, kaydettikten sonra **bambaşka** bir sırayla karşılaşırdı
 * (kullanıcı bildirimi: "A. Harun aslında 2. sırada olması gerekmez mi?").
 *
 * **Bu iki uygulama birlikte değişmelidir.** Sunucu tarafındaki kural değişirse burası da
 * değişmelidir; ayrışırlarsa kayıt öncesi ve sonrası sıralar yeniden birbirinden kopar.
 *
 * <h2>Kod sırası neden düz metin karşılaştırması</h2>
 *
 * Kodlar sıfır dolgulu iki karakterdir (`'01','05','15','20','25',…`), yani metin sırası sayısal
 * sırayla aynıdır. Sayıya çevirme **yapılmaz**: kodlarda harf geçebiliyor (geliştirme ortamında
 * `E5`, `F0` gibi değerler fiilen var) ve çevirme orada `NaN` üretirdi.
 *
 * **Küçük kod daha kıdemlidir** — Genel Müdür `'15'`, Genel Müdür Yardımcıları `'20','21','22'`.
 *
 * <h2>Kodu olmayan SONA gider</h2>
 *
 * Kod boş olabilir (HR'da çözülemeyen sicil). Başa koymak onu "en kıdemli" saymak olurdu.
 */
export function sortParticipants<T extends EmployeePick>(participants: readonly T[]): T[] {
  return [...participants].sort(compareParticipants)
}

/** Tek karşılaştırma noktası — `sortParticipants` bunun üzerinden çalışır. */
function compareParticipants(a: EmployeePick, b: EmployeePick): number {
  const byGroup = compareGroupCode(a.employeeRoleGroupCode, b.employeeRoleGroupCode)
  if (byGroup !== 0) return byGroup

  // Türkçe harf sırası: `localeCompare('tr')` — ham `<`/`>` karşılaştırması kod noktalarına bakar ve
  // "Pelin" < "Özcan" gibi yanlış sonuçlar verir.
  //
  // `sensitivity: 'accent'` — sunucudaki `Collator.SECONDARY` ile AYNI güç. `'base'` kullanmak
  // aksanı yok sayar ("Kâmil" == "Kamil") ve iki uygulama şapkalı ünlü taşıyan adlarda ayrışırdı:
  // sunucu ikisini farklı sıralarken istemci eşit sayıp sicile düşerdi (push kapısı bulgusu
  // 30.09.2026, node ile ölçüldü).
  const byName = fullName(a).localeCompare(fullName(b), 'tr', { sensitivity: 'accent' })
  if (byName !== 0) return byName

  // Son kırıcı sicil. Sunucu tarafı `nullsLast` + ham metin karşılaştırması yapıyor; burada da
  // AYNI politika uygulanır: boş sicil SONA gider ve karşılaştırma yerelden bağımsızdır
  // (siciller rakamdır, yerele duyarlı bir karşılaştırma yalnız ayrışma riski getirirdi).
  return compareEmptyLast(a.employeeId, b.employeeId)
}

/**
 * Boş kod SONA gider; ikisi de boşsa eşit sayılır ve karar ada kalır.
 *
 * **`null` ile boş dize AYNI sayılır** — sunucu tarafı yalnız `null`'ı sona atıyor, boş dizeyi ham
 * metin olarak başa koyardı. Oracle boş dizeyi zaten `NULL`'a çevirdiği için fark bugün
 * ulaşılamaz, ama "aynı kural" diyen iki kodun zıt davranması ileride tuzak olurdu
 * (push kapısı bulgusu 30.09.2026).
 */
function compareGroupCode(a: string | null | undefined, b: string | null | undefined): number {
  return compareEmptyLast(a, b)
}

/** Ortak yardımcı: boş/`null` değer SONA gider, geri kalanı ham metin sırası. */
function compareEmptyLast(a: string | null | undefined, b: string | null | undefined): number {
  const left = a ?? ''
  const right = b ?? ''
  if (left === right) return 0
  if (left === '') return 1
  if (right === '') return -1
  return left < right ? -1 : 1
}

/**
 * Sıralama anahtarı olarak ad — sunucudaki `ParticipantDisplayOrder.fullName` ile **birebir aynı
 * formül**, satır satır: parçalar tek tek `trim`'lenir, aralarına tek boşluk konur, sonuç `trim`'lenir.
 *
 * **`fullName` alanı sıralamada HİÇ KULLANILMAZ** (push kapısı bulguları 30.09.2026, iki tur).
 * Önce önceliklendiriliyordu, sonra "parçalar boşsa yedek" hâline getirilmişti; ikisi de sunucuda
 * **karşılığı olmayan** bir dal üretiyordu — orada yedeklenecek bağımsız bir ad alanı yok
 * (`MEETING_PARTICIPANT`'ta öyle bir kolon bulunmuyor). Parçalar boşken sunucu boş anahtar üretip
 * sicile düşer; istemci de artık aynısını yapar.
 *
 * <p>Pratikte `fullName` zaten bu parçalardan türetiliyor (`pick()` ve `toParticipantDto`), yani
 * yedek dalı gerçek veriyle hiç tetiklenmiyordu. Yine de kaldırıldı: "aynı kural" diyen iki kodun
 * **aynı girdiden aynı çıktıyı** vermesi kanıtlanabilir olmalı, tesadüfe bırakılmamalı.
 */
function fullName(p: EmployeePick): string {
  const first = (p.firstName ?? '').trim()
  const last = (p.lastName ?? '').trim()
  return `${first} ${last}`.trim()
}
